import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const STATUSES = [
  ["under_contract", "Under contract"],
  ["pending_close", "Pending close"],
  ["closed", "Closed"],
  ["fell_through", "Fell through"],
];

/**
 * Edit modal for a Transaction. Changing acceptance_date triggers backend
 * regeneration of deadlines — we warn the agent inline.
 */
export default function TransactionEdit({ tx, onClose, onSaved }) {
  const [f, setF] = useState({
    status: tx.status,
    property_address: tx.property_address || "",
    purchase_price: tx.purchase_price ?? "",
    earnest_money: tx.earnest_money ?? "",
    acceptance_date: tx.acceptance_date || "",
    close_of_escrow_date: tx.close_of_escrow_date || "",
    commission_rate: tx.commission_rate ?? "",
    commission_amount: tx.commission_amount ?? "",
    lender_name: tx.lender_name || "",
    title_company: tx.title_company || "",
    escrow_officer: tx.escrow_officer || "",
    counterparty_name: tx.counterparty_name || "",
    counterparty_email: tx.counterparty_email || "",
    counterparty_phone: tx.counterparty_phone || "",
    notes: tx.notes || "",
  });
  const [saving, setSaving] = useState(false);
  // Compare against the initial anchor dates so either edit triggers
  // the "deadlines will move" hint. The backend does the real math and
  // returns the actual count in the X-Deadlines-Adjusted header.
  const acceptanceChanged = f.acceptance_date !== (tx.acceptance_date || "");
  const coeChanged = f.close_of_escrow_date !== (tx.close_of_escrow_date || "");
  const anchorsMoved = acceptanceChanged || coeChanged;

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f };
      for (const num of ["purchase_price","earnest_money","commission_rate","commission_amount"]) {
        body[num] = body[num] === "" ? null : Number(body[num]);
      }
      for (const s of ["property_address","lender_name","title_company","escrow_officer","notes","counterparty_name","counterparty_email","counterparty_phone"]) {
        body[s] = body[s] || null;
      }
      body.acceptance_date = f.acceptance_date || null;
      body.close_of_escrow_date = f.close_of_escrow_date || null;
      const res = await api.patch(`/transactions/${tx.id}`, body);
      // Backend returns the exact number of deadlines that shifted in
      // the X-Deadlines-Adjusted response header (0 when neither anchor
      // moved). Fallback to the client-side "changed" flag if the
      // header is stripped by a proxy.
      const adjusted = Number(res?.headers?.["x-deadlines-adjusted"] ?? 0);
      if (adjusted > 0) {
        toast.success(`${adjusted} contract deadline${adjusted === 1 ? "" : "s"} have been adjusted based on the new dates`);
      } else if (anchorsMoved) {
        toast.success("Transaction updated · no deadlines needed adjusting");
      } else {
        toast.success("Transaction updated");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center px-4 py-8 overflow-y-auto" role="dialog" aria-label="Edit transaction" data-testid="tx-edit-modal">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between px-6 pt-6 pb-3">
          <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">Edit transaction</h2>
          <button onClick={onClose} className="p-2 rounded-md text-slate-400 hover:bg-slate-100" aria-label="Close" data-testid="tx-edit-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 pt-0 space-y-4">
          <F label="Property address"><input value={f.property_address} onChange={e=>setF({...f, property_address: e.target.value})} className="input" data-testid="tx-edit-address" /></F>
          <F label="Status">
            <select value={f.status} onChange={e=>setF({...f, status: e.target.value})} className="input" data-testid="tx-edit-status">
              {STATUSES.map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select>
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Purchase price"><input type="number" value={f.purchase_price} onChange={e=>setF({...f, purchase_price: e.target.value})} className="input" data-testid="tx-edit-price" /></F>
            <F label="Earnest money"><input type="number" value={f.earnest_money} onChange={e=>setF({...f, earnest_money: e.target.value})} className="input" /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Acceptance date"><input type="date" value={f.acceptance_date} onChange={e=>setF({...f, acceptance_date: e.target.value})} className="input" data-testid="tx-edit-acceptance" /></F>
            <F label="Close of escrow"><input type="date" value={f.close_of_escrow_date} onChange={e=>setF({...f, close_of_escrow_date: e.target.value})} className="input" data-testid="tx-edit-coe" /></F>
          </div>
          {anchorsMoved && (
            <div className="rounded-md border-l-4 border-amber-400 bg-amber-50 p-3 text-xs text-amber-900" data-testid="tx-edit-deadlines-hint">
              Contract deadlines will shift by the same number of days you moved {acceptanceChanged && coeChanged ? "these anchors" : acceptanceChanged ? "acceptance" : "close of escrow"}. Any offsets you edited before are preserved — only the anchor moves.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <F label="Commission rate (%)"><input type="number" step="0.01" value={f.commission_rate} onChange={e=>setF({...f, commission_rate: e.target.value})} className="input" /></F>
            <F label="Commission $"><input type="number" value={f.commission_amount} onChange={e=>setF({...f, commission_amount: e.target.value})} className="input" /></F>
          </div>
          <F label="Lender"><input value={f.lender_name} onChange={e=>setF({...f, lender_name: e.target.value})} className="input" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Title company"><input value={f.title_company} onChange={e=>setF({...f, title_company: e.target.value})} className="input" /></F>
            <F label="Escrow officer"><input value={f.escrow_officer} onChange={e=>setF({...f, escrow_officer: e.target.value})} className="input" /></F>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 -mx-1">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
              Counterparty · other side of the deal
            </div>
            <p className="text-[11px] text-slate-500 mb-3">
              Optional. Used by Smart Fill to auto-populate {tx.side === "seller" ? "Buyer" : "Seller"} fields on contracts — you can still override on a per-document basis.
            </p>
            <F label={`${tx.side === "seller" ? "Buyer" : "Seller"} name`}>
              <input
                value={f.counterparty_name}
                onChange={e=>setF({...f, counterparty_name: e.target.value})}
                className="input"
                data-testid="tx-edit-counterparty-name"
                placeholder={tx.side === "seller" ? "e.g. Nathaniel Cross" : "e.g. Alexandra Whitmore"}
              />
            </F>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <F label="Email">
                <input
                  type="email"
                  value={f.counterparty_email}
                  onChange={e=>setF({...f, counterparty_email: e.target.value})}
                  className="input"
                  data-testid="tx-edit-counterparty-email"
                />
              </F>
              <F label="Phone">
                <input
                  value={f.counterparty_phone}
                  onChange={e=>setF({...f, counterparty_phone: e.target.value})}
                  className="input"
                  data-testid="tx-edit-counterparty-phone"
                />
              </F>
            </div>
          </div>

          <F label="Notes"><textarea value={f.notes} onChange={e=>setF({...f, notes: e.target.value})} className="input min-h-[80px]" /></F>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={onClose} className="text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2">Cancel</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md px-5 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--primary-hex)" }} data-testid="tx-edit-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-widest text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
